import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectGroup, SelectValue, SelectTrigger, SelectContent, SelectLabel, SelectItem } from "@/components/ui/select";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const iso = (d) => d.toISOString().slice(0, 10);
const fmt12 = (t) => {
  if (!t || !/^\d{1,2}:\d{2}/.test(t)) return t;
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
};

const STATUS_COLOR = {
  requested: "#ef4444", confirmed: "#22c55e", completed: "#eab308",
  pending_payment: "#a855f7", rescheduled: "#3b82f6", cancelled: "#94a3b8", no_show: "#f97316",
};
const statusColor = (s) => STATUS_COLOR[s] || "#1f6feb";
const LEGEND = [["requested", "Requested"], ["confirmed", "Confirmed"], ["completed", "Completed"], ["pending_payment", "Awaiting payment"], ["cancelled", "Cancelled"]];
const MGMT_ROLES = ["administrator", "partner", "manager"];
const teamLabel = (t) => (t || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\bUs\b/i, "US");
const initials = (name) => (name || "?").trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
const OWNER_COLORS = ["#0ea5e9", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#ef4444", "#14b8a6", "#6366f1", "#f97316", "#84cc16"];
const ownerColor = (id) => { let h = 0; for (const ch of String(id || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0; return OWNER_COLORS[h % OWNER_COLORS.length]; };

const GCAL_BADGE = {
  synced:       { label: "Synced to Google Calendar", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Icons.CalendarCheck2 },
  pending:      { label: "Syncing…",                  cls: "bg-blue-50 text-blue-600 border-blue-200",          icon: Icons.Loader2 },
  failed:       { label: "Sync failed",               cls: "bg-amber-50 text-amber-700 border-amber-200",       icon: Icons.AlertTriangle },
  auth_failed:  { label: "Reconnection required",     cls: "bg-rose-50 text-rose-700 border-rose-200",          icon: Icons.Link2Off },
  not_required: null,
};

function GcalDetailPanel({ appt, onRetry, retrying }) {
  const gc = appt?.google_calendar || {};
  const status = gc.gcal_sync_status;
  const conf = GCAL_BADGE[status];
  if (!conf) return null;
  const Icon = conf.icon;
  const canRetry = status === "failed" && !gc.gcal_permanent_failure;
  const isAuth   = status === "auth_failed";
  return (
    <div className={`flex items-center justify-between rounded-lg border px-3 py-2 ${conf.cls}`} data-testid={`cal-gcal-panel-${appt.id}`}>
      <div className="flex items-center gap-2 min-w-0">
        <Icon className={`h-3.5 w-3.5 shrink-0 ${status === "pending" ? "animate-spin" : ""}`} />
        <span className="text-xs font-medium">{conf.label}</span>
        {gc.gcal_error && !isAuth && (
          <span className="text-[10px] opacity-60 truncate" title={gc.gcal_error}>{gc.gcal_error.slice(0, 60)}</span>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {gc.html_link && status === "synced" && (
          <a href={gc.html_link} target="_blank" rel="noreferrer" data-testid={`cal-gcal-open-${appt.id}`}
            className="text-[11px] underline">Open</a>
        )}
        {canRetry && (
          <button onClick={() => onRetry(appt.id)} disabled={retrying === appt.id}
            data-testid={`cal-gcal-retry-${appt.id}`}
            className="text-[11px] border border-amber-300 rounded px-2 py-0.5 bg-white hover:bg-amber-50 disabled:opacity-50">
            {retrying === appt.id ? <Icons.Loader2 className="h-3 w-3 animate-spin inline" /> : "Retry sync"}
          </button>
        )}
        {isAuth && (
          <a href="/scheduling/settings" data-testid={`cal-gcal-reconnect-${appt.id}`}
            className="text-[11px] border border-rose-300 rounded px-2 py-0.5 bg-white hover:bg-rose-50">
            Reconnect
          </a>
        )}
      </div>
    </div>
  );
}

export default function SchedulingCalendar() {
  const { user } = useAuth();
  const isMgmt = MGMT_ROLES.includes(user?.role);
  const [cursor, setCursor] = useState(new Date());
  const [appts, setAppts] = useState([]);
  const [view, setView] = useState("month");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [staff, setStaff] = useState([]);
  const [owner, setOwner] = useState("all");
  const [reschedOpen, setReschedOpen] = useState(false);
  const [reschedNote, setReschedNote] = useState("");
  const [retrying, setRetrying] = useState(null);

  // Load the firm's staff (managers/admins only) to power the calendar switcher.
  useEffect(() => {
    if (!isMgmt) return;
    api.get("/scheduling/staff").then((r) => setStaff(r.data.staff || [])).catch(() => setStaff([]));
  }, [isMgmt]);

  const teams = [...new Set(staff.map((s) => s.team).filter(Boolean))].sort();
  const staffById = Object.fromEntries(staff.map((s) => [s.id, s]));
  // Show the owner chip whenever more than one person's events can appear (All / team views).
  const showOwnerChip = isMgmt && owner !== "mine" && !owner.startsWith("staff:");
  const OwnerChip = ({ id, size = "sm" }) => {
    const s = staffById[id];
    if (!showOwnerChip || !id) return null;
    const dim = size === "sm" ? "h-4 w-4 text-[8px]" : "h-6 w-6 text-[10px]";
    return (
      <span title={s?.name || "Unassigned"} data-testid={`cal-owner-chip-${id}`}
        className={`shrink-0 ${dim} rounded-full flex items-center justify-center font-bold text-white ring-1 ring-white/50`}
        style={{ backgroundColor: ownerColor(id) }}>
        {initials(s?.name)}
      </span>
    );
  };

  const load = useCallback(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const params = { from_date: iso(first), to_date: iso(last) };
    if (owner === "mine") params.mine = true;
    else if (owner.startsWith("staff:")) params.staff_id = owner.slice(6);
    else if (owner.startsWith("team:")) params.team_id = owner.slice(5);
    api.get("/scheduling/appointments", { params })
      .then((r) => setAppts(r.data.appointments || [])).catch(() => setAppts([]));
  }, [cursor, owner]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setBusy(true);
    try {
      await api.post(`/scheduling/appointments/${id}/status`, { status });
      toast.success(status === "confirmed" ? "Appointment confirmed — the client has been notified." : `Marked ${status}`);
      setSelected(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const sendRescheduleRequest = async () => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      await api.post(`/scheduling/appointments/${selected.id}/request-reschedule`, { note: reschedNote.trim() });
      toast.success("Reschedule request sent — the client has been asked to pick a new time.");
      setReschedOpen(false); setReschedNote(""); setSelected(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send request"); }
    finally { setBusy(false); }
  };

  const retrySync = async (id) => {
    setRetrying(id);
    try {
      await api.post(`/scheduling/appointments/${id}/sync`);
      toast.success("Google Calendar sync retried.");
      load();
      // Refresh selected if it's the same appointment
      if (selected?.id === id) {
        const r = await api.get("/scheduling/appointments");
        const updated = (r.data.appointments || []).find((a) => a.id === id);
        if (updated) setSelected(updated);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Sync retry failed"); }
    finally { setRetrying(null); }
  };

  const closeDetail = () => { setSelected(null); setReschedOpen(false); setReschedNote(""); };

  const byDate = {};
  appts.forEach((a) => { (byDate[a.date] = byDate[a.date] || []).push(a); });

  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [...Array(startPad).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const today = iso(new Date());

  return (
    <div className="p-6 max-w-[1280px] mx-auto space-y-4" data-testid="scheduling-calendar">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-slate-900">Calendar</h1>
          {isMgmt && (
            <Select value={owner} onValueChange={setOwner}>
              <SelectTrigger className="w-[220px] h-9" data-testid="cal-owner-select">
                <div className="flex items-center gap-2 min-w-0">
                  <Icons.Users className="h-4 w-4 text-slate-400 shrink-0" />
                  <SelectValue placeholder="All calendars" />
                </div>
              </SelectTrigger>
              <SelectContent className="max-h-[360px]">
                <SelectItem value="all" data-testid="cal-owner-all">All calendars</SelectItem>
                <SelectItem value="mine" data-testid="cal-owner-mine">My calendar</SelectItem>
                {teams.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>Teams</SelectLabel>
                    {teams.map((t) => <SelectItem key={t} value={`team:${t}`} data-testid={`cal-owner-team-${t}`}>{teamLabel(t)}</SelectItem>)}
                  </SelectGroup>
                )}
                <SelectGroup>
                  <SelectLabel>People</SelectLabel>
                  {staff.map((s) => (
                    <SelectItem key={s.id} value={`staff:${s.id}`} data-testid={`cal-owner-staff-${s.id}`}>{s.name}</SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            {["month", "agenda"].map((v) => <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`} className={`text-sm px-3 py-1.5 rounded-md capitalize ${view === v ? "bg-white shadow-sm text-slate-800" : "text-slate-500"}`}>{v}</button>)}
          </div>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-2 hover:bg-slate-100 rounded"><Icons.ChevronLeft className="h-4 w-4" /></button>
          <span className="text-sm font-medium text-slate-700 w-36 text-center">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</span>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-2 hover:bg-slate-100 rounded"><Icons.ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid="cal-legend">
        {LEGEND.map(([k, label]) => (
          <span key={k} className="inline-flex items-center gap-1.5 text-xs text-slate-500">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: statusColor(k) }} />{label}
          </span>
        ))}
      </div>

      {view === "month" ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="text-xs font-medium text-slate-400 px-2 py-2 text-center">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((day, i) => {
              const ds = day ? iso(new Date(cursor.getFullYear(), cursor.getMonth(), day)) : null;
              const items = ds ? (byDate[ds] || []) : [];
              return (
                <div key={i} className={`min-h-[96px] border-b border-r border-slate-100 p-1.5 ${ds === today ? "bg-blue-50/40" : ""}`} data-testid={ds ? `cal-day-${ds}` : undefined}>
                  {day && <div className={`text-xs mb-1 ${ds === today ? "font-bold text-blue-600" : "text-slate-400"}`}>{day}</div>}
                  {items.slice(0, 3).map((a) => (
                    <button key={a.id} onClick={() => setSelected(a)} className="w-full flex items-center gap-1 text-left text-[11px] rounded px-1.5 py-0.5 mb-0.5 text-white" style={{ backgroundColor: statusColor(a.status) }}>
                      <span className="truncate flex-1">{a.start_time} {a.meeting_type_name}</span>
                      <OwnerChip id={a.assigned_staff_id} />
                    </button>
                  ))}
                  {items.length > 3 && <div className="text-[10px] text-slate-400 px-1">+{items.length - 3} more</div>}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100" data-testid="cal-agenda">
          {appts.length === 0 ? <p className="p-6 text-sm text-slate-400">No appointments this month.</p> :
            appts.map((a) => (
              <button key={a.id} onClick={() => setSelected(a)} className="w-full text-left flex items-center gap-3 p-3 hover:bg-slate-50">
                <span className="w-1.5 h-9 rounded-full" style={{ backgroundColor: statusColor(a.status) }} />
                <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-800 truncate">{a.meeting_type_name} · {a.contact_name}</div><div className="text-xs text-slate-400">{a.date} {a.start_time} · {a.status}</div></div>
                <OwnerChip id={a.assigned_staff_id} size="lg" />
              </button>
            ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={closeDetail}>
          <div className="bg-white rounded-xl max-w-md w-full p-5 space-y-2" onClick={(e) => e.stopPropagation()} data-testid="cal-appt-detail">
            <div className="flex items-center justify-between"><h3 className="font-heading font-semibold text-slate-800">{selected.meeting_type_name}</h3><button onClick={closeDetail}><Icons.X className="h-4 w-4 text-slate-400" /></button></div>
            <p className="text-sm text-slate-600">{selected.contact_name} · {selected.number}</p>
            {(() => { const sn = staff.find((s) => s.id === selected.assigned_staff_id); return (sn || selected.assigned_team) ? (
              <p className="text-xs text-slate-500 inline-flex items-center gap-1.5" data-testid="cal-appt-owner">
                <Icons.User className="h-3.5 w-3.5 text-slate-400" />
                {sn ? sn.name : "Unassigned"}{selected.assigned_team ? ` · ${teamLabel(selected.assigned_team)}` : ""}
              </p>
            ) : null; })()}
            <p className="text-sm text-slate-500">{selected.date} at {fmt12(selected.start_time)} ({selected.timezone}) · {selected.duration_min} min</p>
            {selected.google_calendar?.gcal_sync_status && (
              <GcalDetailPanel appt={selected} onRetry={retrySync} retrying={retrying} />
            )}
            {selected.previous_start_time && (
              <div className="text-xs bg-blue-50 text-blue-700 rounded-lg px-3 py-2 leading-relaxed" data-testid="cal-appt-reschedule-note">
                <span className="font-semibold inline-flex items-center gap-1"><Icons.CalendarClock className="h-3.5 w-3.5" />Rescheduled</span>
                {" from "}<span className="line-through opacity-70">{selected.previous_date} at {fmt12(selected.previous_start_time)}</span>
                {" → now "}<span className="font-medium">{selected.date} at {fmt12(selected.start_time)}</span>
                {selected.rescheduled_at && (() => {
                  const hist = selected.reschedule_history || [];
                  const by = hist.length ? hist[hist.length - 1].by : null;
                  return <span className="block text-blue-500 mt-1">Changed {new Date(selected.rescheduled_at).toLocaleString()}{by ? ` · by ${by}` : ""}</span>;
                })()}
              </div>
            )}
            {(selected.zoom || {}).join_url && <a href={selected.zoom.join_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-sky-600"><Icons.Video className="h-4 w-4" />Join Zoom</a>}
            <p className="text-xs"><span className="px-2 py-0.5 rounded-full text-white capitalize" style={{ backgroundColor: statusColor(selected.status) }}>{selected.status.replace("_", " ")}</span></p>
            <div className="pt-2 border-t border-slate-100">
              {reschedOpen ? (
                <div className="space-y-2" data-testid="cal-reschedule-request-panel">
                  <p className="text-sm text-slate-600">This will <span className="font-medium">cancel this appointment</span>, email {selected.contact_name || "the client"} an apology, and invite them to pick a new time in their Secure Portal.</p>
                  <textarea value={reschedNote} onChange={(e) => setReschedNote(e.target.value)} data-testid="cal-reschedule-note-input" rows={3}
                    placeholder="Optional note — e.g. Mitch is out sick today. Leave blank for the standard 'unforeseen circumstances' message."
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 resize-none" />
                  <div className="flex gap-2">
                    <button onClick={() => { setReschedOpen(false); setReschedNote(""); }} className="flex-1 text-sm border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-lg px-3 py-2">Back</button>
                    <button onClick={sendRescheduleRequest} disabled={busy} data-testid="cal-reschedule-send-btn"
                      className="flex-1 inline-flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg px-3 py-2 disabled:opacity-60">
                      {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Send className="h-4 w-4" />}Send request
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  {selected.status === "requested" && (
                    <button onClick={() => setStatus(selected.id, "confirmed")} disabled={busy} data-testid="cal-confirm-btn"
                      className="flex-1 inline-flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg px-3 py-2 disabled:opacity-60">
                      <Icons.CheckCircle2 className="h-4 w-4" />Accept & Confirm
                    </button>
                  )}
                  {["requested", "confirmed", "rescheduled"].includes(selected.status) && (
                    <button onClick={() => setStatus(selected.id, "completed")} disabled={busy} data-testid="cal-complete-btn"
                      className="inline-flex items-center gap-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600 text-sm rounded-lg px-3 py-2 disabled:opacity-60"><Icons.Check className="h-4 w-4" />Complete</button>
                  )}
                  {["requested", "confirmed", "rescheduled"].includes(selected.status) && (
                    <button onClick={() => { setReschedOpen(true); setReschedNote(""); }} disabled={busy} data-testid="cal-request-reschedule-btn"
                      className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-blue-300 hover:text-blue-600 text-slate-600 text-sm rounded-lg px-3 py-2 disabled:opacity-60"><Icons.CalendarClock className="h-4 w-4" />Request reschedule</button>
                  )}
                  {["requested", "confirmed", "rescheduled"].includes(selected.status) && (
                    <button onClick={() => setStatus(selected.id, "cancelled")} disabled={busy} data-testid="cal-cancel-btn"
                      className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-rose-300 hover:text-rose-600 text-slate-600 text-sm rounded-lg px-3 py-2 disabled:opacity-60"><Icons.X className="h-4 w-4" />Cancel</button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
